import { memo, useState } from "react"
import { IconCheckOutline18 } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	Button,
	Popover,
	POPOVER_AUTOFOCUS_ATTRIBUTE,
	Shortcut,
	Textarea,
} from "@productlane/ui"

import { useShortcut } from "@/lib"
import { DECLINE_NOTE_PLACEHOLDER } from "./format"

// A hint in the button's own colour, quieter than its label.
export const ButtonKeys = ({ children }: { children: string }) => (
	<span
		aria-hidden="true"
		className="ml-1.5 text-xs font-normal tracking-normal opacity-50"
	>
		{children}
	</span>
)

// Typing a 1 or 2 into the composer or an inline edit must never decide a card.
export function isTyping(event: KeyboardEvent) {
	const node = event.target
	return (
		node instanceof HTMLElement &&
		!!node.closest(
			'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
		)
	)
}

// The reason and note go to the docs agent, which turns them into a rule.
export const DeclineButton = memo(
	({
		label,
		reasons,
		disabled,
		side = "bottom",
		shortcut = false,
		onDecline,
	}: {
		label: string
		reasons: readonly string[]
		disabled?: boolean
		side?: "top" | "bottom"
		// 2 opens the reasons; 1 accepts on the same card.
		shortcut?: boolean
		onDecline: (reason: string, note: string) => void
	}) => {
		const [open, setOpen] = useState(false)
		const [reason, setReason] = useState<string | null>(null)
		const [note, setNote] = useState("")

		useShortcut("2", () => setOpen(true), {
			enabled: shortcut && !disabled && !open,
			// The note takes focus on open; without this the same keystroke types a 2 into it.
			preventDefault: true,
			ignoreEventWhen: isTyping,
		})

		const submit = () => {
			// Every reason list ends with "Something else", the reason when none is picked.
			onDecline(reason ?? reasons.at(-1) ?? "Something else", note.trim())
			setOpen(false)
			setReason(null)
			setNote("")
		}

		return (
			<Popover
				open={open}
				onOpenChange={setOpen}
				align="end"
				side={side}
				className="w-72 p-1"
				content={
					<div className="flex flex-col">
						<div
							role="radiogroup"
							aria-label="Reason"
							className="flex flex-col"
						>
							{reasons.map((option) => (
								<button
									key={option}
									type="button"
									role="radio"
									aria-checked={reason === option}
									onClick={() => setReason(option)}
									className="flex h-8 items-center justify-between gap-2 rounded-md px-2.5 text-left text-sm text-secondary hover:bg-gray-100 hover:text-primary focus-visible:bg-gray-100 focus-visible:outline-none aria-checked:text-primary dark:hover:bg-gray-750 dark:focus-visible:bg-gray-750"
								>
									{option}
									<IconCheckOutline18
										aria-hidden
										className={cn(
											"size-4 flex-none transform-gpu",
											reason !== option && "invisible",
										)}
									/>
								</button>
							))}
						</div>
						<div className="mt-1 flex flex-col gap-2 border-t border-gray-200 p-1.5 pt-2.5 dark:border-gray-700/80">
							<Textarea
								// Focus starts in the note, so no reason looks picked on open.
								{...{ [POPOVER_AUTOFOCUS_ATTRIBUTE]: true }}
								name="decline-note"
								value={note}
								placeholder={DECLINE_NOTE_PLACEHOLDER}
								minRows={2}
								maxRows={5}
								onChange={(event) => setNote(event.target.value)}
								onKeyDown={(event) => {
									if (
										event.key === "Enter" &&
										(event.metaKey || event.ctrlKey)
									) {
										event.preventDefault()
										submit()
									}
								}}
							/>
							<Button
								data-testid="decline-submit"
								className="self-end"
								onClick={submit}
							>
								{label}
								<ButtonKeys>{`${ShortcutSymbols.CMD}${ShortcutSymbols.ENTER}`}</ButtonKeys>
							</Button>
						</div>
					</div>
				}
			>
				<Button variant="ghost" disabled={disabled}>
					{label}
					{shortcut && <Shortcut className="ml-1.5">2</Shortcut>}
				</Button>
			</Popover>
		)
	},
)
